import { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";


const defaultHeader = {
  obra: "Demolição - Rua Crixas, 48 - Santana,SP",
  cliente: "Rosangela e Carlinhos",
  responsavel: "Engestrutural",
  mes: new Date().toISOString().slice(0, 7),
};

const newRow = (id) => ({
  id,
  data: "",
  descricao: "",
  fornecedor: "",
  quantidade: "",
  unidade: "un",
  valorUnit: "",
  nota: "",
  anexos: [], // { name, type, dataUrl }
});

const unidades = ["un", "m²", "m³", "m", "kg", "L", "sc", "cx", "pç", "vb"];

const formatBRL = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return "";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const parseBRL = (val) =>
  parseFloat(String(val).replace(/[^\d,]/g, "").replace(",", ".")) || 0;

// ── Salva/carrega do localStorage ──────────────────────────────────────────
function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    // localStorage pode estar cheio (arquivos grandes)
    console.warn("Erro ao salvar:", e);
  }
}

// ── Componente principal ────────────────────────────────────────────────────
export default function App() {
  const saved = loadState();

  const [rows, setRows] = useState(
    saved?.rows ?? [newRow(1)]
  );
  const [nextId, setNextId] = useState(saved?.nextId ?? 2);
  const [header, setHeader] = useState(saved?.header ?? defaultHeader);
  const [editingHeader, setEditingHeader] = useState(false);
  const [activeAnexo, setActiveAnexo] = useState(null); // id da linha com modal aberto
  const [activeTab, setActiveTab] = useState("planilha"); // "planilha" | "anexos"
  const fileInputRef = useRef();

  // Auto-save sempre que dados mudam
  useEffect(() => {
    saveState({ rows, nextId, header });
  }, [rows, nextId, header]);

  // ── Ações de linha ────────────────────────────────────────────────────────
  const addRow = () => {
    const r = newRow(nextId);
    setRows((prev) => [...prev, r]);
    setNextId((n) => n + 1);
  };

  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));

  const updateRow = (id, field, value) =>
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );

  // ── Anexos ────────────────────────────────────────────────────────────────
  const handleFileAdd = (rowId, files) => {
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const anexo = { name: file.name, type: file.type, dataUrl: e.target.result };
        setRows((prev) =>
          prev.map((r) =>
            r.id === rowId
              ? { ...r, anexos: [...(r.anexos || []), anexo] }
              : r
          )
        );
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAnexo = (rowId, idx) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? { ...r, anexos: r.anexos.filter((_, i) => i !== idx) }
          : r
      )
    );
  };

  // ── Totais ────────────────────────────────────────────────────────────────
  const total = rows.reduce((acc, r) => {
    const qty = parseFloat(r.quantidade) || 0;
    const unit = parseBRL(r.valorUnit);
    return acc + qty * unit;
  }, 0);

  const mesFormatado = header.mes
    ? new Date(header.mes + "-01").toLocaleDateString("pt-BR", {
        month: "long",
        year: "numeric",
      })
    : "";

  // ── Export Excel ──────────────────────────────────────────────────────────
  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const infoRows = [
      ["PLANILHA DE COMPRA DE MATERIAIS"],
      [],
      ["Obra:", header.obra],
      ["Cliente:", header.cliente],
      ["Responsável:", header.responsavel],
      ["Período:", mesFormatado],
      ["Gerado em:", new Date().toLocaleDateString("pt-BR")],
      [],
      ["#", "Data", "Descrição", "Fornecedor", "Qtd", "Un", "Valor Unit.", "Total Item", "Observação", "Anexos"],
    ];
    rows.forEach((r, idx) => {
      const qty = parseFloat(r.quantidade) || 0;
      const unit = parseBRL(r.valorUnit);
      const itemTotal = qty * unit;
      const dataFmt = r.data
        ? new Date(r.data + "T00:00:00").toLocaleDateString("pt-BR")
        : "";
      const anexosNomes = (r.anexos || []).map((a) => a.name).join(", ");
      infoRows.push([
        idx + 1, dataFmt, r.descricao, r.fornecedor,
        qty || "", r.unidade, unit || "", itemTotal || "", r.nota, anexosNomes,
      ]);
    });
    infoRows.push([]);
    infoRows.push(["", "", "", "", "", "", "TOTAL GERAL:", total, "", ""]);
    const ws = XLSX.utils.aoa_to_sheet(infoRows);
    ws["!cols"] = [
      { wch: 4 }, { wch: 12 }, { wch: 30 }, { wch: 25 },
      { wch: 10 }, { wch: 6 }, { wch: 14 }, { wch: 14 }, { wch: 22 }, { wch: 30 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, "Materiais");
    XLSX.writeFile(wb, `Planilha_Materiais_${header.mes || "export"}.xlsx`);
  };

  // ── Todos os itens com anexos (para aba Anexos) ───────────────────────────
  const rowsComAnexos = rows.filter((r) => (r.anexos || []).length > 0);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* HEADER */}
        <div style={styles.header}>
          <div>
            <div style={styles.headerLabel}>Relatório de Compras</div>
            <div style={styles.headerTitle}>Planilha de Materiais</div>
            <div style={styles.headerSub}>{mesFormatado}</div>
          </div>
          <div style={{ textAlign: "right", fontSize: 13, color: "#cbd5e1", lineHeight: 1.9 }}>
            {editingHeader ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                {[
                  ["obra", "Obra"],
                  ["cliente", "Cliente"],
                  ["responsavel", "Responsável"],
                ].map(([field, label]) => (
                  <input
                    key={field}
                    value={header[field]}
                    onChange={(e) => setHeader((h) => ({ ...h, [field]: e.target.value }))}
                    placeholder={label}
                    style={styles.inputHeader}
                  />
                ))}
                <input
                  type="month"
                  value={header.mes}
                  onChange={(e) => setHeader((h) => ({ ...h, mes: e.target.value }))}
                  style={styles.inputHeader}
                />
                <button onClick={() => setEditingHeader(false)} style={styles.btnSalvar}>
                  ✓ Salvar
                </button>
              </div>
            ) : (
              <>
                <div><span style={{ color: "#64748b" }}>Obra:</span> {header.obra}</div>
                <div><span style={{ color: "#64748b" }}>Cliente:</span> {header.cliente}</div>
                <div><span style={{ color: "#64748b" }}>Responsável:</span> {header.responsavel}</div>
                <button onClick={() => setEditingHeader(true)} style={styles.btnEditar}>
                  ✏️ Editar
                </button>
              </>
            )}
          </div>
        </div>

        {/* ABAS */}
        <div style={styles.tabs}>
          {[
            { key: "planilha", label: "📋 Planilha de Compras" },
            { key: "anexos", label: `📎 Anexos / NF / Boletos (${rowsComAnexos.length})` },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                ...styles.tab,
                ...(activeTab === tab.key ? styles.tabActive : {}),
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ABA: PLANILHA */}
        {activeTab === "planilha" && (
          <>
            <div style={styles.card}>
              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr style={{ background: "#1a1a2e", color: "#fff" }}>
                      {["#", "Data", "Descrição do Material", "Fornecedor", "Qtd", "Un", "Valor Unit.", "Total Item", "Observação", "📎", ""].map(
                        (h, i) => (
                          <th key={i} style={{ ...styles.th, textAlign: i >= 4 && i <= 7 ? "center" : "left" }}>
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => {
                      const qty = parseFloat(r.quantidade) || 0;
                      const unit = parseBRL(r.valorUnit);
                      const itemTotal = qty * unit;
                      const nAnexos = (r.anexos || []).length;
                      return (
                        <tr
                          key={r.id}
                          style={{ background: idx % 2 === 0 ? "#f8fafc" : "#fff" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#eef4ff")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "#f8fafc" : "#fff")}
                        >
                          <td style={{ ...styles.td, color: "#94a3b8", fontWeight: 700, textAlign: "center" }}>{idx + 1}</td>
                          <td style={styles.td}>
                            <input type="date" value={r.data} onChange={(e) => updateRow(r.id, "data", e.target.value)} style={{ ...styles.cellInput, width: 120 }} />
                          </td>
                          <td style={styles.td}>
                            <input value={r.descricao} onChange={(e) => updateRow(r.id, "descricao", e.target.value)} placeholder="Ex: Cimento CP-II" style={{ ...styles.cellInput, width: 180 }} />
                          </td>
                          <td style={styles.td}>
                            <input value={r.fornecedor} onChange={(e) => updateRow(r.id, "fornecedor", e.target.value)} placeholder="Ex: Casa do Construtor" style={{ ...styles.cellInput, width: 150 }} />
                          </td>
                          <td style={{ ...styles.td, textAlign: "center" }}>
                            <input type="number" value={r.quantidade} onChange={(e) => updateRow(r.id, "quantidade", e.target.value)} placeholder="0" style={{ ...styles.cellInput, width: 60, textAlign: "center" }} />
                          </td>
                          <td style={{ ...styles.td, textAlign: "center" }}>
                            <select value={r.unidade} onChange={(e) => updateRow(r.id, "unidade", e.target.value)} style={{ ...styles.cellInput, width: 58 }}>
                              {unidades.map((u) => <option key={u}>{u}</option>)}
                            </select>
                          </td>
                          <td style={{ ...styles.td, textAlign: "center" }}>
                            <input value={r.valorUnit} onChange={(e) => updateRow(r.id, "valorUnit", e.target.value)} placeholder="0,00" style={{ ...styles.cellInput, width: 90, textAlign: "right" }} />
                          </td>
                          <td style={{ ...styles.td, textAlign: "right", fontWeight: 700, color: itemTotal > 0 ? "#1a1a2e" : "#cbd5e1", whiteSpace: "nowrap" }}>
                            {itemTotal > 0 ? formatBRL(itemTotal) : "—"}
                          </td>
                          <td style={styles.td}>
                            <input value={r.nota} onChange={(e) => updateRow(r.id, "nota", e.target.value)} placeholder="Opcional" style={{ ...styles.cellInput, width: 130 }} />
                          </td>
                          {/* Botão anexo */}
                          <td style={{ ...styles.td, textAlign: "center" }}>
                            <button
                              onClick={() => setActiveAnexo(r.id)}
                              title="Gerenciar anexos"
                              style={{
                                ...styles.btnAnexo,
                                background: nAnexos > 0 ? "#16a34a" : "#e2e8f0",
                                color: nAnexos > 0 ? "#fff" : "#64748b",
                              }}
                            >
                              📎 {nAnexos > 0 ? nAnexos : "+"}
                            </button>
                          </td>
                          <td style={{ ...styles.td, textAlign: "center" }}>
                            <button onClick={() => removeRow(r.id)} style={styles.btnRemove}>✕</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={styles.addRowBar}>
                <button onClick={addRow} style={styles.btnAddRow}>+ Adicionar Item</button>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>💾 Salvo automaticamente</span>
              </div>
            </div>

            {/* RODAPÉ */}
            <div style={styles.footer}>
              <button onClick={exportExcel} style={styles.btnExcel}>
                <span style={{ fontSize: 20 }}>⬇</span> Baixar Excel (.xlsx)
              </button>
              <div style={styles.totalBox}>
                <div style={styles.totalLabel}>Total Geral do Período</div>
                <div style={styles.totalValue}>{formatBRL(total) || "R$ 0,00"}</div>
                <div style={styles.totalSub}>
                  {rows.length} {rows.length === 1 ? "item" : "itens"} cadastrado{rows.length !== 1 ? "s" : ""}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ABA: ANEXOS */}
        {activeTab === "anexos" && (
          <div style={styles.card}>
            {rowsComAnexos.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📎</div>
                <div style={{ fontSize: 15 }}>Nenhum anexo ainda.</div>
                <div style={{ fontSize: 13, marginTop: 6 }}>
                  Clique no ícone 📎 na planilha para adicionar NF, boleto ou foto de cada item.
                </div>
              </div>
            ) : (
              <div style={{ padding: 16 }}>
                {rowsComAnexos.map((r) => {
                  const idx = rows.findIndex((x) => x.id === r.id);
                  return (
                    <div key={r.id} style={styles.anexoGroup}>
                      <div style={styles.anexoGroupTitle}>
                        <span style={styles.anexoIdx}>#{idx + 1}</span>
                        <span>{r.descricao || "(sem descrição)"}</span>
                        <span style={{ color: "#94a3b8", fontSize: 12, marginLeft: 8 }}>{r.fornecedor}</span>
                      </div>
                      <div style={styles.anexoList}>
                        {r.anexos.map((a, i) => (
                          <div key={i} style={styles.anexoItem}>
                            {a.type.startsWith("image/") ? (
                              <a href={a.dataUrl} target="_blank" rel="noreferrer">
                                <img src={a.dataUrl} alt={a.name} style={styles.anexoThumb} />
                              </a>
                            ) : (
                              <a href={a.dataUrl} download={a.name} style={styles.anexoFile}>
                                {a.type === "application/pdf" ? "📄" : "📎"} {a.name}
                              </a>
                            )}
                            <button onClick={() => removeAnexo(r.id, i)} style={styles.btnRemoveAnexo}>✕</button>
                          </div>
                        ))}
                        <label style={styles.btnAddAnexo}>
                          + Adicionar
                          <input type="file" multiple style={{ display: "none" }} onChange={(e) => handleFileAdd(r.id, e.target.files)} accept="image/*,.pdf" />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div style={styles.creditos}>
          Documento gerado em {new Date().toLocaleDateString("pt-BR")} · Uso interno — relatório ao cliente
        </div>
      </div>

      {/* MODAL ANEXOS POR LINHA */}
      {activeAnexo !== null && (() => {
        const row = rows.find((r) => r.id === activeAnexo);
        if (!row) return null;
        const idx = rows.findIndex((r) => r.id === activeAnexo);
        return (
          <div style={styles.modalOverlay} onClick={() => setActiveAnexo(null)}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    #{idx + 1} — {row.descricao || "(sem descrição)"}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>Anexos: NF, Boleto, Foto</div>
                </div>
                <button onClick={() => setActiveAnexo(null)} style={styles.modalClose}>✕</button>
              </div>

              <div style={styles.modalBody}>
                {(row.anexos || []).length === 0 ? (
                  <div style={{ textAlign: "center", color: "#94a3b8", padding: 24 }}>
                    Nenhum arquivo anexado ainda.
                  </div>
                ) : (
                  <div style={styles.anexoList}>
                    {row.anexos.map((a, i) => (
                      <div key={i} style={styles.anexoItem}>
                        {a.type.startsWith("image/") ? (
                          <a href={a.dataUrl} target="_blank" rel="noreferrer">
                            <img src={a.dataUrl} alt={a.name} style={styles.anexoThumb} />
                          </a>
                        ) : (
                          <a href={a.dataUrl} download={a.name} style={styles.anexoFile}>
                            {a.type === "application/pdf" ? "📄" : "📎"} {a.name}
                          </a>
                        )}
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{a.name}</div>
                        <button onClick={() => removeAnexo(row.id, i)} style={styles.btnRemoveAnexo}>✕ remover</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={styles.modalFooter}>
                <label style={styles.btnAddAnexoModal}>
                  📎 Adicionar arquivo (NF, Boleto, Foto)
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileAdd(row.id, e.target.files)}
                  />
                </label>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>
                  Aceita: imagens e PDF
                </span>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Estilos ─────────────────────────────────────────────────────────────────
const styles = {
  page: { fontFamily: "'Segoe UI', sans-serif", background: "#f0f2f5", minHeight: "100vh", padding: "24px 16px" },
  container: { maxWidth: 1150, margin: "0 auto" },

  header: {
    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)",
    borderRadius: 12, padding: "28px 32px", marginBottom: 0,
    color: "#fff", display: "flex", justifyContent: "space-between",
    alignItems: "flex-start", flexWrap: "wrap", gap: 12,
    borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
  },
  headerLabel: { fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "#a0b4d6", marginBottom: 6 },
  headerTitle: { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  headerSub: { fontSize: 13, color: "#a0b4d6", textTransform: "capitalize" },

  inputHeader: {
    background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: 6, color: "#fff", padding: "5px 10px", fontSize: 12, width: 220, outline: "none",
  },
  btnSalvar: {
    background: "#e2a818", color: "#000", border: "none", borderRadius: 6,
    padding: "4px 14px", fontSize: 11, cursor: "pointer", fontWeight: 700,
  },
  btnEditar: {
    marginTop: 8, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
    color: "#fff", borderRadius: 6, padding: "4px 12px", fontSize: 11, cursor: "pointer",
  },

  tabs: { display: "flex", background: "#e2e8f0", borderRadius: 0, overflow: "hidden" },
  tab: {
    flex: 1, padding: "11px 16px", border: "none", background: "transparent",
    fontSize: 13, cursor: "pointer", fontWeight: 600, color: "#64748b", transition: "all .2s",
  },
  tabActive: { background: "#fff", color: "#1a1a2e", borderBottom: "3px solid #0f3460" },

  card: {
    background: "#fff", borderRadius: 0, boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
    overflow: "hidden", marginBottom: 20,
    borderBottomLeftRadius: 12, borderBottomRightRadius: 12,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    padding: "12px 10px", fontWeight: 600, fontSize: 11,
    letterSpacing: 1, textTransform: "uppercase", whiteSpace: "nowrap",
  },
  td: { padding: "6px 8px" },
  cellInput: {
    border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 8px",
    fontSize: 13, outline: "none", background: "#fff", color: "#1e293b",
  },
  addRowBar: {
    padding: "12px 16px", borderTop: "1px solid #f1f5f9",
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  btnAddRow: {
    background: "#1a1a2e", color: "#fff", border: "none", borderRadius: 7,
    padding: "8px 18px", fontSize: 13, cursor: "pointer", fontWeight: 600,
  },
  btnRemove: { background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16 },
  btnAnexo: {
    border: "none", borderRadius: 6, padding: "4px 8px",
    fontSize: 12, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap",
  },

  footer: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 },
  btnExcel: {
    background: "linear-gradient(135deg, #16a34a, #15803d)", color: "#fff", border: "none",
    borderRadius: 10, padding: "14px 28px", fontSize: 15, cursor: "pointer", fontWeight: 700,
    boxShadow: "0 4px 14px rgba(22,163,74,0.35)", display: "flex", alignItems: "center", gap: 10,
  },
  totalBox: {
    background: "linear-gradient(135deg, #1a1a2e, #0f3460)", borderRadius: 12,
    padding: "20px 32px", color: "#fff", minWidth: 260, textAlign: "right",
  },
  totalLabel: { fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#a0b4d6", marginBottom: 6 },
  totalValue: { fontSize: 28, fontWeight: 700 },
  totalSub: { fontSize: 12, color: "#64748b", marginTop: 4 },

  creditos: { textAlign: "center", marginTop: 8, marginBottom: 8, fontSize: 11, color: "#94a3b8" },

  // Anexos na aba
  anexoGroup: { marginBottom: 24, borderBottom: "1px solid #f1f5f9", paddingBottom: 16 },
  anexoGroupTitle: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontWeight: 600, fontSize: 14 },
  anexoIdx: {
    background: "#1a1a2e", color: "#fff", borderRadius: 4,
    padding: "2px 7px", fontSize: 12, fontWeight: 700,
  },
  anexoList: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-start" },
  anexoItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, maxWidth: 120 },
  anexoThumb: { width: 90, height: 90, objectFit: "cover", borderRadius: 8, border: "2px solid #e2e8f0", cursor: "pointer" },
  anexoFile: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 12,
    background: "#f1f5f9", borderRadius: 8, padding: "8px 12px",
    color: "#1a1a2e", textDecoration: "none", wordBreak: "break-word", maxWidth: 120,
  },
  btnRemoveAnexo: {
    background: "none", border: "1px solid #fca5a5", borderRadius: 4,
    color: "#ef4444", fontSize: 11, cursor: "pointer", padding: "2px 6px",
  },
  btnAddAnexo: {
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "#f8fafc", border: "2px dashed #cbd5e1", borderRadius: 8,
    padding: "10px 14px", fontSize: 12, color: "#64748b", cursor: "pointer", fontWeight: 600,
    minWidth: 100, minHeight: 60, textAlign: "center",
  },

  // Modal
  modalOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  },
  modal: {
    background: "#fff", borderRadius: 14, width: "min(90vw, 560px)",
    maxHeight: "80vh", display: "flex", flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  },
  modalHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9",
  },
  modalClose: {
    background: "#f1f5f9", border: "none", borderRadius: 6,
    color: "#64748b", fontSize: 16, cursor: "pointer", padding: "4px 10px",
  },
  modalBody: { flex: 1, overflowY: "auto", padding: "16px 24px" },
  modalFooter: {
    padding: "16px 24px", borderTop: "1px solid #f1f5f9",
    display: "flex", flexDirection: "column", gap: 8,
  },
  btnAddAnexoModal: {
    display: "block", textAlign: "center", background: "#1a1a2e", color: "#fff",
    border: "none", borderRadius: 8, padding: "12px", fontSize: 14,
    cursor: "pointer", fontWeight: 600,
  },
};
